// Tells the reader whether a task or evidence page shows the frozen record or the live one
// (spec J5 step 4, §8.3). Frozen: the label and date, read-only, a way back to live. Live: a
// note when the record changed after the latest freeze, with a way to see it as frozen.
import { formatLocal } from '@cis/shared';
import type { FreezeEntityType } from '@cis/api-client';
import { useAfterFreeze, useFreezeDetail } from './queries.ts';
import './freeze.css';

export interface FreezeBannerProps {
  readonly entityType: FreezeEntityType;
  readonly entityKey: string;
  /** From useFreezeParam(): the freeze shown, or null for the live record. */
  readonly freezeId: number | null;
  readonly onSelectFreeze: (id: number | null) => void;
}

function FrozenBanner(props: FreezeBannerProps & { readonly freezeId: number }): React.JSX.Element {
  const detail = useFreezeDetail(props.freezeId);
  const what =
    detail.data === undefined
      ? 'a freeze'
      : `the freeze "${detail.data.label}" of ${formatLocal(detail.data.at)}`;
  return (
    <div className="freeze-banner freeze-banner--frozen" role="status">
      <p className="freeze-banner__text">
        You are looking at this record as it was at {what}. It cannot be changed here.
      </p>
      <button
        type="button"
        className="btn btn--secondary"
        onClick={() => props.onSelectFreeze(null)}
      >
        Show the live record
      </button>
    </div>
  );
}

function LiveBanner(props: FreezeBannerProps): React.JSX.Element | null {
  const mark = useAfterFreeze(props.entityType, props.entityKey);
  const freeze = mark.data?.freeze;
  if (mark.data?.afterFreeze !== true || freeze === null || freeze === undefined) return null;
  return (
    <div className="freeze-banner" role="status">
      <p className="freeze-banner__text">
        After freeze: this record changed after the freeze "{freeze.label}" of{' '}
        {formatLocal(freeze.at)}.
      </p>
      <button
        type="button"
        className="btn btn--secondary"
        onClick={() => props.onSelectFreeze(freeze.id)}
      >
        Show it as frozen
      </button>
    </div>
  );
}

export function FreezeBanner(props: FreezeBannerProps): React.JSX.Element | null {
  const { freezeId } = props;
  return freezeId === null ? (
    <LiveBanner {...props} />
  ) : (
    <FrozenBanner {...props} freezeId={freezeId} />
  );
}
